<template>
  <v-container class="px-10" fluid>
    <!-- Breadcrumbs -->
    <v-row>
      <v-col>
        <v-breadcrumbs
          :items="breadcrumbs"
          divider=">"
          class="px-0"
        ></v-breadcrumbs>
        <div class="mb-4">
          <h3 class="pageHeadline mb-1" t-data="page-headline">
            Appointments <span class="thai-subtitle">นัดหมายแพทย์</span>
          </h3>
          <p class="page-description mb-0">
            Manage doctor appointment bookings submitted from all hospital branch websites.
          </p>
        </div>
      </v-col>
    </v-row>

      <!-- COMBINED TABLE CARD -->
      <v-card class="table-card" elevation="0">
        <!-- Top Filter Bar inside the Card -->
        <div class="filter-bar pa-4">
          <!-- Row 1: Search & Dropdowns in ONE Single Row -->
          <div class="filter-row-1 d-flex align-center gap-2 mb-3">
            <!-- 1. Global Search Box -->
            <v-text-field
              v-model="searchQuery"
              placeholder="Search by Reference No, Patient Name, Phone, or National ID..."
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              color="primary"
              class="filter-search-field flex-grow-1"
              @keyup.enter="handleSearch"
              @input="onSearchInput"
              @click:clear="clearSearch"
            ></v-text-field>
            <!-- 2. Branch Dropdown -->
            <v-select
              v-model="selectedBranch"
              :items="branchSelectItems"
              item-text="name"
              item-value="codename"
              outlined
              dense
              hide-details
              color="primary"
              item-color="primary"
              :disabled="isBranchDisabled || currentRole === 'branch_admin'"
              class="filter-select-field branch-select"
              @change="onBranchChange"
            ></v-select>
            <!-- 3. Status Dropdown -->
            <v-select
              v-model="selectedStatus"
              :items="statusOptions"
              item-text="text"
              item-value="value"
              outlined
              dense
              hide-details
              color="primary"
              item-color="primary"
              class="filter-select-field status-select"
              @change="handleFilterChange"
            ></v-select>
            <!-- 4. Department Dropdown -->
            <v-select
              v-model="selectedDepartment"
              :items="departmentSelectItems"
              item-text="name"
              item-value="id"
              outlined
              dense
              hide-details
              color="primary"
              item-color="primary"
              class="filter-select-field dept-select"
              @change="onDepartmentChange"
            ></v-select>
            <!-- 5. Doctor Dropdown -->
            <v-select
              v-model="selectedDoctor"
              :items="doctorSelectItems"
              item-text="name"
              item-value="id"
              outlined
              dense
              hide-details
              color="primary"
              item-color="primary"
              class="filter-select-field doctor-select"
              @change="onDoctorChange"
            ></v-select>
          </div>

          <!-- Row 2: Date Range Picker & Filter Action Button -->
          <div class="filter-row-2 d-flex align-center gap-3">
            <!-- Date Range Box with Calendar Menu -->
            <v-menu
              v-model="dateMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="290px"
            >
              <template v-slot:activator="{ on, attrs }">
                <div
                  class="date-range-container d-flex align-center"
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon
                    small
                    class="date-icon mr-2"
                    style="cursor: pointer;"
                  >
                    mdi-calendar-range-outline
                  </v-icon>
                  <input
                    v-model="dateFrom"
                    type="text"
                    placeholder="dd/mm/yyyy"
                    class="date-input"
                    @change="onManualDateChange"
                  />
                  <span class="date-separator mx-2">—</span>
                  <input
                    v-model="dateTo"
                    type="text"
                    placeholder="dd/mm/yyyy"
                    class="date-input"
                    @change="onManualDateChange"
                  />
                </div>
              </template>
              <v-date-picker
                v-model="pickerDates"
                range
                no-title
                scrollable
                color="primary"
                @input="onPickerDateSelect"
              ></v-date-picker>
            </v-menu>

            <!-- Filter Button -->
            <v-btn
              outlined
              class="filter-btn text-none"
              :class="{'has-filtered': hasAppliedFilters}"
              @click="handleFilterButtonClick"
            >
              <v-icon small left>{{ hasAppliedFilters ? 'mdi-close' : 'mdi-filter-variant' }}</v-icon>
              <span>{{ hasAppliedFilters ? 'Clear' : 'Filter' }}</span>
            </v-btn>
          </div>
        </div>

        <!-- Data Table (Sticky Fixed Header & Inner Scroll) -->
        <v-data-table
          :loading="isLoading || isTableLoading || isInitialLoading"
          :headers="table.headers"
          :items="appointmentList"
          :items-per-page="table.pageSize"
          :server-items-length="totalAppointments"
          hide-default-footer
          fixed-header
          class="appointment-table"
          @click:row="openAppointmentDetail"
        >
          <!-- 1. REF NO. -->
          <template v-slot:item.apt_code="{ item }">
            <span class="ref-code">
              {{ item.apt_code || item.apt_ref_no || '-' }}
            </span>
          </template>
          <!-- 2. DATE & TIME -->
          <template v-slot:item.appointmentDateTime="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="date-text">{{ formatTableDate(item.apt_date) }}</span>
              <span class="time-text">
                {{ formatTableTime(item.apt_start_time) }} - {{ formatTableTime(item.apt_end_time) }}
              </span>
            </div>
          </template>
          <!-- 3. BRANCH -->
          <template v-slot:item.org="{ item }">
            <span class="branch-tag">
              {{ getBranchCode(item) }}
            </span>
          </template>
          <!-- 4. PATIENT NAME -->
          <template v-slot:item.customer="{ item }">
            <span class="patient-name font-weight-bold">
              {{ getPatientName(item) }}
            </span>
          </template>
          <!-- 5. DOCTOR / DEPARTMENT -->
          <template v-slot:item.doctor_department="{ item }">
            <div class="d-flex flex-column py-2">
              <span class="doctor-name">{{ getDoctorName(item) }}</span>
              <span class="dept-text">{{ getDepartmentName(item) }}</span>
            </div>
          </template>
          <!-- 6. BOOKING TYPE -->
          <template v-slot:item.booking_type="{ item }">
            <span :class="['type-pill', bookingTypeClass(item)]">
              {{ formatBookingType(item) }}
            </span>
          </template>
          <!-- 7. STATUS -->
          <template v-slot:item.is_status="{ item }">
            <span class="status-pill" :class="statusClass(item.is_status)">
              <span class="dot"></span>
              {{ formatStatusLabel(item.is_status) }}
            </span>
          </template>
          <!-- 8. ACTIONS -->
          <template v-slot:item.actions="{ item }">
            <button class="icon-btn" @click.stop="openAppointmentDetail(item)">
              <v-icon size="16">mdi-eye-outline</v-icon>
            </button>
          </template>
          <!-- No Data State -->
          <template v-slot:no-data>
            <div class="py-10 text-center grey--text">
              <v-icon size="44" color="grey lighten-1">mdi-calendar-blank-outline</v-icon>
              <div class="mt-2 text-caption font-weight-medium">No appointments found</div>
            </div>
          </template>
        </v-data-table>

        <!-- Custom Table Footer with Pagination -->
        <div class="table-foot">
          <span>Showing {{ pageStart }} to {{ pageEnd }} of {{ totalAppointments }} entries</span>
          <v-pagination
            v-model="paginationPage"
            :length="pageLength"
            :total-visible="7"
            color="primary"
            @input="onPageChange"
          ></v-pagination>
        </div>
      </v-card>

      <!-- Appointment Detail Dialog Component -->
      <appointment-detail
        :visible="appointmentDetailOpen"
        :appointment-item="selectedAppointment"
        @close="closeAppointmentDetail"
        @saved="onDetailSaved"
      />
  </v-container>
</template>

<script>
import { mapState, mapActions } from 'vuex'
import client from '@/apis/http-client'
import appointmentDetail from './appointmentDetail.vue'
let pathUrl = `/por/api`

export default {
  name: 'AppointmentList',
  components: {
    appointmentDetail
  },
  data() {
    return {
      breadcrumbs: [
        { text: 'Dashboard', exact: true, href: '/' },
        { text: 'Appointments', disabled: true, href: '/appointments' }
      ],
      isInitialLoading: false,
      isTableLoading: false,
      currentRole: 'super_admin',
      searchQuery: '',
      searchDebounceTimer: null,
      selectedBranch: '',
      selectedStatus: '',
      selectedDepartment: '',
      selectedDoctor: '',
      doctorOptions: [],
      dateFrom: '',
      dateTo: '',
      dateMenu: false,
      pickerDates: [],
      statusOptions: [
        { text: 'All Status', value: '' },
        { text: 'Pending', value: 'pending' },
        { text: 'Confirmed', value: 'confirmed' },
        { text: 'Completed', value: 'completed' },
        { text: 'Cancelled', value: 'cancelled' }
      ],
      appointmentDetailOpen: false,
      selectedAppointment: null,
      paginationPage: 1,
      table: {
        pageSize: 25,
        headers: [
          { text: 'REF NO.', value: 'apt_code', sortable: false, width: '170px' },
          { text: 'DATE & TIME', value: 'appointmentDateTime', sortable: false, width: '130px' },
          { text: 'BRANCH', value: 'org', sortable: false, width: '90px' },
          { text: 'PATIENT NAME', value: 'customer', sortable: false, width: '160px' },
          { text: 'DOCTOR / DEPARTMENT', value: 'doctor_department', sortable: false, width: '180px' },
          { text: 'BOOKING TYPE', value: 'booking_type', sortable: false, width: '120px' },
          { text: 'STATUS', value: 'is_status', sortable: false, width: '130px' },
          { text: 'ACTIONS', value: 'actions', sortable: false, align: 'center', width: '80px' }
        ]
      }
    }
  },
  computed: {
    ...mapState('auth', ['user']),
    ...mapState('appointment', [
      'appointmentList',
      'totalAppointments',
      'isLoading',
      'orgList',
      'departmentList'
    ]),

    isBranchDisabled() {
       const user = this.user || {}
       return !user.is_superuser
    },

    branchCount() {
      return (this.orgList && this.orgList.all && this.orgList.all.length)
    },

    branchSelectItems() {
      if (this.isBranchDisabled || this.currentRole === 'branch_admin') {
        const userOrg = this.getUserOrgCode()
        return [{ name: (userOrg || '-').toUpperCase(), codename: userOrg }]
      }
      const allOrgs = (this.orgList && this.orgList.all) || []
      return [
        { name: `All Branches (${this.branchCount})`, codename: '' },
        ...allOrgs.map(org => ({
          name: (org.codename || '').toUpperCase(),
          codename: org.codename
        }))
      ]
    },

    departmentSelectItems() {
      return [
        { name: 'All Departments', id: '' },
        ...(this.departmentList || []).map(dept => ({
          name: dept.name || dept.title || dept.department_name || `Dept #${dept.id || dept.department_id}`,
          id: dept.id || dept.department_id
        }))
      ]
    },

    doctorSelectItems() {
      return [
        { name: 'All Doctors', id: '' },
        ...(this.doctorOptions || [])
      ]
    },

    activeFilterCount() {
      let count = 0
      if (this.selectedStatus) count += 1
      if (this.selectedDepartment) count += 1
      if (this.selectedDoctor) count += 1
      if (this.dateFrom || this.dateTo) count += 1
      if (!this.isBranchDisabled && this.selectedBranch) count += 1
      if (this.searchQuery && this.searchQuery.trim()) count += 1
      return count
    },

    hasAppliedFilters() {
      return this.activeFilterCount > 0
    },

    pageLength() {
      const size = (this.table && this.table.pageSize) || 25
      return Math.max(1, Math.ceil((this.totalAppointments || 0) / size))
    },
    pageStart() {
      if (!this.totalAppointments) return 0
      const size = (this.table && this.table.pageSize) || 25
      return (this.paginationPage - 1) * size + 1
    },
    pageEnd() {
      if (!this.totalAppointments) return 0
      const size = (this.table && this.table.pageSize) || 25
      return Math.min(this.paginationPage * size, this.totalAppointments)
    }
  },
  async mounted() {
    if (this.setListOrgInfo) {
      await this.setListOrgInfo()
    }

    if (this.isBranchDisabled) {
      this.currentRole = 'branch_admin'
      this.selectedBranch = this.getUserOrgCode()
    } else {
      this.currentRole = 'super_admin'
      this.selectedBranch = ''
    }

    this.syncBranchFilterToStore()
    await Promise.all([
      this.fetchData().finally(() => {
        this.isInitialLoading = false
      }),
      this.loadDepartments(),
      this.loadDoctors()
    ])
  },
  watch: {
    paginationPage(newPage) {
      this.onPageChange(newPage)
    }
  },
  methods: {
    ...mapActions('appointment', [
      'fetch',
      'fetchDepartment',
      'setListOrgInfo',
      'setTextSearch',
      'setFilterStatus',
      'setFilterDepartment',
      'setFilterDoctor',
      'setFilterDates',
      'setPage'
    ]),

    getUserOrgCode() {
      const u = this.user || {}
      const code = u.org_code || u.org_codename || u.org || u.assigned_branch || (u.organization && u.organization.codename)
      return (code || '').toLowerCase()
    },

    syncBranchFilterToStore() {
      if (this.$store.state.appointment && this.$store.state.appointment.orgList) {
        this.$store.state.appointment.orgList.selected = this.selectedBranch
          ? [{ codename: this.selectedBranch, selected: true }]
          : []
      }
    },

    async loadDepartments() {
      if (this.fetchDepartment) {
        await this.fetchDepartment(this.selectedBranch ? [this.selectedBranch] : [])
      }
    },

    async onBranchChange() {
      this.$store.commit('appointment/SET_IS_LOADING', true)
      this.selectedDepartment = ''
      this.selectedDoctor = ''
      this.handleFilterChange()
      await Promise.all([
        this.loadDepartments(),
        this.loadDoctors()
      ])
    },

    async onDepartmentChange() {
      this.$store.commit('appointment/SET_IS_LOADING', true)
      this.selectedDoctor = ''
      this.handleFilterChange()
      await this.loadDoctors()
    },

    onDoctorChange() {
      this.$store.commit('appointment/SET_IS_LOADING', true)
      this.handleFilterChange()
    },

    async loadDoctors() {
      try {
        const params = {
          page: 1,
          limit: 25,
          ...(this.selectedBranch && { org_codename: [this.selectedBranch] }),
          ...(this.selectedDepartment && { department_id: this.selectedDepartment })
        }
        const response = await client.secureHttpClient.get(`${pathUrl}/doctors`, { params })
        if (response && response.data) {
          const results = response.data.results || response.data.data || []
          this.doctorOptions = results.map(doc => {
            let name = ''
            if (doc.contents && doc.contents.length) {
              const content = doc.contents.find(c => c.code === 'en') || doc.contents[0]
              name = `${content.prefix_name || ''} ${content.name || ''}`.trim()
            } else {
              name = doc.name || doc.doctor_name || `Doctor #${doc.id || doc.doctor_id}`
            }
            return {
              name: name || `Doctor #${doc.id || doc.doctor_id}`,
              id: doc.id || doc.doctor_id
            }
          })
        }
      } catch (e) {
        this.doctorOptions = []
      }
    },

    onPickerDateSelect(dates) {
      if (dates && dates.length > 0) {
        const [start, end] = [...dates].sort()
        this.dateFrom = this.formatInputDate(start)
        this.dateTo = end ? this.formatInputDate(end) : ''

        if (dates.length === 2) {
          this.dateMenu = false
          this.applyFilters()
        }
      }
    },

    formatInputDate(isoDate) {
      if (!isoDate) return ''
      const parts = isoDate.split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : isoDate
    },

    parseDateToISO(formattedDate) {
      if (!formattedDate) return null
      const parts = formattedDate.trim().split('/')
      if (parts.length === 3) {
        return `${parts[2]}-${parts[1].padStart(2, '0')}-${parts[0].padStart(2, '0')}`
      }
      return formattedDate.includes('-') ? formattedDate : null
    },

    onManualDateChange() {
      const startDate = this.parseDateToISO(this.dateFrom)
      const endDate = this.parseDateToISO(this.dateTo)
      this.pickerDates = [startDate, endDate].filter(Boolean)
    },

    applyFilters() {
      const startDate = this.parseDateToISO(this.dateFrom)
      const endDate = this.parseDateToISO(this.dateTo)
      if (this.setFilterDates) {
        this.setFilterDates({ startDate, endDate })
      }
      this.paginationPage = 1
      if (this.setPage) {
        this.setPage(1)
      }
      this.fetchData()
    },

    async clearFilters() {
      this.$store.commit('appointment/SET_IS_LOADING', true)
      this.selectedStatus = ''
      this.selectedDepartment = ''
      this.selectedDoctor = ''
      this.dateFrom = ''
      this.dateTo = ''
      this.pickerDates = []
      this.searchQuery = ''

      if (!this.isBranchDisabled) {
        this.selectedBranch = ''
        this.syncBranchFilterToStore()
      }

      if (this.setFilterDates) this.setFilterDates({ startDate: null, endDate: null })
      if (this.setTextSearch) this.setTextSearch('')
      if (this.setFilterStatus) this.setFilterStatus(null)
      if (this.setFilterDepartment) this.setFilterDepartment(null)
      if (this.setFilterDoctor) this.setFilterDoctor(null)

      this.paginationPage = 1
      if (this.setPage) this.setPage(1)

      await Promise.all([
        this.fetchData(),
        this.loadDepartments(),
        this.loadDoctors()
      ])
    },

    async handleFilterButtonClick() {
      if (this.hasAppliedFilters) {
        await this.clearFilters()
        return
      }
      await this.applyFilters()
    },

    formatTableDate(dateStr) {
      if (!dateStr) return '-'
      const parts = String(dateStr).split('T')[0].split('-')
      return parts.length === 3 ? `${parts[2]}/${parts[1]}/${parts[0]}` : dateStr
    },

    formatTableTime(timeStr) {
      return !timeStr ? '--:--' : String(timeStr).substring(0, 5)
    },

    getBranchCode(item) {
      if (!item) return '-'
      return ((item.org && (item.org.codename || item.org.name)) || item.org_codename || '-').toUpperCase()
    },

    getPatientName(item) {
      if (item && item.customer) {
        const c = item.customer
        const prefix = c.prefix || ''
        const name = c.fullname || `${c.first_name || ''} ${c.last_name || ''}`.trim()
        return prefix ? `${prefix} ${name}` : name || '-'
      }
      return (item && item.cust_fullname) || '-'
    },

    getDoctorName(item) {
      if (!item || !item.doctor) return '-'
      if (typeof item.doctor === 'string') return item.doctor
      return item.doctor.name || (item.doctor.content && item.doctor.content.name) || '-'
    },

    getDepartmentName(item) {
      if (!item || !item.department) return '-'
      if (typeof item.department === 'string') return item.department
      return item.department.department_name || (item.department.content && item.department.content.title) || '-'
    },

    isBookedForOther(item) {
      if (!item) return false
      const cp = item.contact_person || item
      const hasContact = Boolean(
        (cp.contact_firstname && cp.contact_firstname.trim()) ||
        (cp.contact_lastname && cp.contact_lastname.trim()) ||
        (cp.contact_phone && cp.contact_phone.trim()) ||
        (cp.contact_email && cp.contact_email.trim())
      )
      if (hasContact) return true
      if (item.booking_type) {
        const bookingType = String(item.booking_type).toLowerCase()
        if (bookingType === 'other' || bookingType === 'for other' || bookingType === 'for_other') return true
        if (bookingType === 'self' || bookingType === 'for self') return false
      }
      return Boolean(item.booking_for_other)
    },

    formatBookingType(item) {
      return this.isBookedForOther(item) ? 'For Other' : 'Self'
    },

    bookingTypeClass(item) {
      return this.isBookedForOther(item) ? 'type-pill-other' : 'type-pill-self'
    },

    formatStatusLabel(status) {
      if (!status) return 'Pending'
      const map = { pending: 'Pending', confirmed: 'Confirmed', completed: 'Completed', cancelled: 'Cancelled' }
      return map[String(status).toLowerCase()] || status
    },

    statusClass(status) {
      const map = { confirmed: 'status-green', pending: 'status-orange', cancelled: 'status-red', completed: 'status-gray' }
      return map[String(status || '').toLowerCase()] || 'status-orange'
    },

    handleFilterChange() {
      this.syncBranchFilterToStore()
      if (this.setFilterStatus) this.setFilterStatus(this.selectedStatus || null)
      if (this.setFilterDepartment) this.setFilterDepartment(this.selectedDepartment || null)
      if (this.setFilterDoctor) this.setFilterDoctor(this.selectedDoctor || null)
      this.paginationPage = 1
      if (this.setPage) this.setPage(1)
      this.fetchData()
    },

    onPageChange(newPage) {
      this.paginationPage = newPage
      if (this.setPage) this.setPage(newPage)
      this.fetchData()
    },

    onSearchInput() {
      clearTimeout(this.searchDebounceTimer)
      this.searchDebounceTimer = setTimeout(() => {
        this.handleSearch()
      }, 400)
    },

    handleSearch() {
      if (this.setTextSearch) {
        this.setTextSearch(this.searchQuery ? this.searchQuery.trim() : '')
      }
      this.fetchData()
    },

    clearSearch() {
      this.searchQuery = ''
      if (this.setTextSearch) {
        this.setTextSearch('')
      }
      this.fetchData()
    },

    async fetchData() {
      this.isTableLoading = true
      try {
        if (this.fetch) {
          await this.fetch()
        }
      } finally {
        this.isTableLoading = false
      }
    },

    openAppointmentDetail(item) {
      if (!item || !item.id) return
      this.selectedAppointment = { ...item }
      this.appointmentDetailOpen = true
    },

    closeAppointmentDetail() {
      this.appointmentDetailOpen = false
      this.selectedAppointment = null
    },

    async onDetailSaved() {
      this.appointmentDetailOpen = false
      this.selectedAppointment = null
      await this.fetchData()
    }
  }
}
</script>

<style scoped>
/* Page Header */
.thai-subtitle {
  font-weight: 400;
  font-size: 16px;
  color: #64748b;
  margin-left: 6px;
}

.page-description {
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0;
}

/* Combined Card & Filter Bar */
.table-card {
  border: 1px solid #e6e9f0 !important;
  border-radius: 10px !important;
  overflow: hidden;
  background-color: #ffffff !important;
}

.filter-bar {
  border-bottom: 1px solid #eef2f7;
}

.filter-row-1 {
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
  width: 100%;
}

.gap-2 { gap: 8px; }
.gap-3 { gap: 12px; }

.filter-search-field {
  min-width: 220px;
}

.filter-select-field {
  flex: 0 0 auto;
}

.filter-select-field.branch-select { width: 165px; }
.filter-select-field.status-select { width: 130px; }
.filter-select-field.dept-select { width: 165px; }
.filter-select-field.doctor-select { width: 140px; }

/* Date Range Container */
.date-range-container {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.38);
  border-radius: 4px;
  padding: 0 12px;
  height: 40px !important;
  min-height: 40px !important;
  width: 275px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  transition: border-color 0.2s cubic-bezier(0.25, 0.8, 0.25, 1);
}

.date-range-container:hover {
  border-color: rgba(0, 0, 0, 0.87);
}

.date-range-container:focus-within {
  border-color: var(--primary-500-color) !important;
  box-shadow: 0 0 0 1px var(--primary-500-color) !important;
}

.date-icon {
  color: rgba(0, 0, 0, 0.54) !important;
  font-size: 18px !important;
}

.date-input {
  border: none;
  background: transparent;
  outline: none;
  font-size: 14px !important;
  font-family: inherit;
  width: 100px;
  color: rgba(0, 0, 0, 0.87);
  text-align: center;
  line-height: 20px;
}

.date-input::placeholder {
  color: rgba(0, 0, 0, 0.42) !important;
  font-size: 14px !important;
  font-weight: 400;
}

.date-separator {
  color: rgba(0, 0, 0, 0.42);
  font-size: 14px;
  margin: 0 4px;
}

/* Filter Action Button */
.filter-btn {
  text-transform: none;
  font-weight: 600;
  border-color: #e2e8f0 !important;
  height: 40px !important;
  border-radius: 8px !important;
}

.filter-btn.has-filtered {
  color: #ef4444 !important;
  border-color: #fca5a5 !important;
  background-color: #fef2f2 !important;
}

/* Data Table */
.appointment-table {
  width: 100%;
}

.appointment-table ::v-deep thead tr:not(.v-data-table__progress) th {
  font-size: 12px !important;
  color: #64748b !important;
  font-weight: 700 !important;
  background-color: #ffffff !important;
  border-bottom: 1px solid #eef2f7 !important;
}

.appointment-table ::v-deep .v-data-table__progress th {
  height: 0 !important;
  padding: 0 !important;
  border: none !important;
  background: transparent !important;
}

.appointment-table ::v-deep .v-data-table__progress {
  height: 3px !important;
}

.appointment-table ::v-deep td {
  font-size: 14px !important;
  color: #0f172a !important;
  height: 56px !important;
  border-bottom: 1px solid #f1f5f9 !important;
}

.appointment-table ::v-deep tbody tr {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.appointment-table ::v-deep tbody tr:hover {
  background-color: #f8fafc !important;
}

.appointment-table ::v-deep .v-data-table__wrapper {
  max-height: calc(100vh - 330px);
  overflow-y: auto;
  overflow-x: auto;
}

/* REF NO. */
.ref-code {
  font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, 'Liberation Mono', 'Courier New', monospace;
  font-size: 13px;
  font-weight: 700;
  color: #0f172a;
}

/* Date & Time */
.date-text {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.time-text {
  font-size: 11.5px;
  color: #64748b;
}

/* Branch Tag */
.branch-tag {
  display: inline-flex;
  align-items: center;
  font-size: 11.5px;
  font-weight: 700;
  letter-spacing: 0.2px;
  padding: 4px 9px;
  border-radius: 6px;
  background: #eef1f6;
  color: #5b6472;
}

/* Patient Name */
.patient-name {
  color: #0f172a;
  font-size: 13.5px;
}

/* Doctor / Department */
.doctor-name {
  font-size: 13px;
  font-weight: 600;
  color: #0f172a;
}

.dept-text {
  font-size: 11.5px;
  color: #64748b;
}

/* Booking Type Pill */
.type-pill {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 6px;
  padding: 4px 10px;
  font-size: 12.5px;
  font-weight: 700;
  line-height: 1.2;
}

.type-pill-self {
  background-color: #ebf3fe !important;
  color: #1e40af !important;
}

.type-pill-other {
  background-color: #f5eeff !important;
  color: #7c3aed !important;
}

/* Status Pill with Dot */
.status-pill {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 4px 10px;
  border-radius: 12px;
  font-weight: 700;
  font-size: 12px;
}

.status-pill .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
}

.status-pill.status-green {
  background: #f0fdf4;
  color: #15803d;
}
.status-pill.status-green .dot {
  background: #1aa964;
}

.status-pill.status-orange {
  background: #fffbeb;
  color: #b45309;
}
.status-pill.status-orange .dot {
  background: #f59e0b;
}

.status-pill.status-red {
  background: #fef2f2;
  color: #b91c1c;
}
.status-pill.status-red .dot {
  background: #ef4444;
}

.status-pill.status-gray {
  background: #f8fafc;
  color: #64748b;
}
.status-pill.status-gray .dot {
  background: #94a3b8;
}

/* Action Eye Button */
.icon-btn {
  width: 30px;
  height: 30px;
  border-radius: 7px;
  border: 1px solid #e6e9f0;
  background: #fff;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
}

.icon-btn:hover {
  background: #f8fafc;
  border-color: var(--primary-500-color);
  color: var(--primary-500-color);
}

/* Table Footer & Pagination */
.table-foot {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 16px;
  font-size: 12px;
  color: #64748b;
  border-top: 1px solid #eef2f7;
}
</style>
